import { Box, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import LocationIcon from '../../../../../assets/icons/location-icon.svg';
import { LANG_TEXT } from '../../../../../constants/language';
import { useAuthContext } from '../../../../../context/authContext';
import { useOrderContext } from '../../../../../context/orderContext';
import { formatSeatInfo } from '../../../../../utils/multiOrder.utils';
import { Order } from '../../../order.types';

const Transition: React.FC<{ item: Order; type: string; seatDataMap?: Map<string, any> }> = ({ item, type, seatDataMap: propSeatDataMap }) => {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const { restaurantList, seatDataMap: contextSeatDataMap } = useOrderContext();
  const seatDataMap = propSeatDataMap || contextSeatDataMap;

  const restaurants = useMemo(() => {
    const foods = type === 'promotion' ? (item?.promotion_foods ?? []) : (item?.foods ?? []);
    const uniqueRestaurantIds = Array.from(new Set(foods.map((food) => food.restaurantId).filter(Boolean)));

    if (uniqueRestaurantIds.length === 0) {
      const defaultId = item?.multi_orders?.[0]?.restaurant_id || item?.restaurant_id;
      if (!defaultId) return [];
      uniqueRestaurantIds.push(defaultId);
    }

    const myRestaurantIds = uniqueRestaurantIds.filter((restaurantId) => {
      if (!item?.multi_orders || item.multi_orders.length === 0) {
        return !item?.rider_id || item?.rider_id === user?.uid;
      }

      const multiOrder = item.multi_orders.find((mo) => mo.restaurant_id === restaurantId);
      return multiOrder && multiOrder.rider_id === user?.uid;
    });

    const restaurantIds = myRestaurantIds?.length > 0 ? myRestaurantIds : uniqueRestaurantIds;

    return restaurantIds
      .map((id) => {
        const restaurant = restaurantList?.find((r) => r.restaurant_id === id);
        return type === 'promotion' ? item?.restaurant_promotion : restaurant || item?.restaurant;
      })
      .filter(Boolean);
  }, [item, restaurantList, type, user?.uid]);

  const information = useMemo(() => {
    if (!item.order_table_number || !seatDataMap) return null;
    return seatDataMap.get(item.order_table_number) || null;
  }, [item.order_table_number, seatDataMap]);

  return (
    <Box bgcolor="white" padding="16px" display="flex" gap="16px" borderBottom="1px solid #EAECF0">
      <Box display="flex" flexDirection="column" gap="4px" alignItems="center" padding="8px 0">
        <Box border="3px solid #3D9F4A" height="12px" width="12px" boxSizing="border-box" borderRadius="999px" />
        <Box borderRight="1px dashed #3D9F4A" borderLeft="1px dashed #3D9F4A" height="calc(100% - 32px)" width="1px" boxSizing="border-box" />
        <Box border="3px solid #3D9F4A" height="12px" width="12px" boxSizing="border-box" borderRadius="999px" />
      </Box>

      <Box display="flex" flexDirection="column" gap="16px">
        <Box display="flex" flexDirection="column" gap="8px">
          {restaurants.map((restaurant, index) => (
            <Box key={`${restaurant?.restaurant_id}-${index}`}>
              <Typography fontSize="18px" lineHeight="26px" color="#1D2939" fontWeight="500">
                {restaurant?.name || '-'}
              </Typography>
              <Box display="flex" gap="8px" alignItems="center">
                <img src={LocationIcon} width={17} height={17} alt="user" />
                <Typography fontSize="16px" lineHeight="24px" color="#667085" fontWeight="500">
                  {restaurant?.region || '-'}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
        <Box display="flex" gap="8px" flexDirection="column">
          <Typography fontSize="18px" lineHeight="26px" color="#1D2939" fontWeight="500">
            {t(LANG_TEXT.ORDER_SEAT_NUMBER)}: {information ? formatSeatInfo(information) : '-'}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Transition;
